import React, { useState, useEffect } from 'react';
import { Stepper } from '../../../../../components/ui/stepper';
import { Brain, Scan, CheckCircle2, Shield, FileText, Loader2 } from 'lucide-react';
import type { Step } from '../types';

interface ClassifyingPhaseProps {
  steps: Step[];
  currentStep: number;
  selectedFile: File | null;
}

const phases = [
  { label: 'Content Analysis', desc: 'Scanning structure', icon: Scan, color: 'blue' },
  { label: 'Framework Detection', desc: 'Identifying type', icon: Brain, color: 'indigo' },
  { label: 'Validation', desc: 'Quality check', icon: CheckCircle2, color: 'purple' },
] as const;

export const ClassifyingPhase: React.FC<ClassifyingPhaseProps> = ({
  steps,
  currentStep,
  selectedFile,
}) => {
  const [activePhase, setActivePhase] = useState(0);

  // Cycle through phases to show activity
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhase((prev) => (prev < 2 ? prev + 1 : prev));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-6xl mx-auto">
      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg shrink-0">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Document Analysis</h1>
            <p className="text-xs text-slate-500">Classifying document structure and content</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-11 sm:ml-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            <Loader2 className="w-3 h-3 animate-spin" />
            Analyzing
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
            <Shield className="w-3 h-3" /> Vastabloc Secure
          </span>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <Stepper steps={steps} currentStep={currentStep} size="sm" showSpinner={true} />
      </div>

      {/* Main Analysis Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Spinner */}
          <div className="relative shrink-0">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-[3px] border-blue-100 border-t-blue-600 animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>

          {/* File + Progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg min-w-0">
                <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">
                  {selectedFile?.name}
                </span>
                <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                  {selectedFile?.name.split('.').pop()?.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
              <div
                className="absolute inset-y-0 left-0 bg-blue-600 rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(33 + activePhase * 25, 80)}%` }}
              />
            </div>

            <p className="text-xs text-slate-500">
              Phase {activePhase + 1} of 3: {phases[activePhase]?.label}
            </p>
          </div>
        </div>
      </div>

      {/* Phase Steps */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        {phases.map((phase, index) => {
          const Icon = phase.icon;
          const isDone = index < activePhase;
          const isCurrent = index === activePhase;

          return (
            <div
              key={index}
              className={`flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border transition-all duration-500 ${
                isDone
                  ? 'border-emerald-200 bg-emerald-50'
                  : isCurrent
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  isDone
                    ? 'bg-emerald-100 text-emerald-600'
                    : isCurrent
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isDone ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : isCurrent ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${isDone ? 'text-emerald-800' : isCurrent ? 'text-blue-800' : 'text-slate-500'}`}>
                  {phase.label}
                </p>
                <p className={`text-[10px] ${isDone ? 'text-emerald-600' : isCurrent ? 'text-blue-600' : 'text-slate-400'}`}>
                  {isDone ? 'Complete' : phase.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
